// The node "doing" a SELF-REFERENTIAL step — a self-call (`A > A.x`), a note,
// an await, or a `go` marker: rows whose from/to collapse to one lane and so
// draw NO edge on the board. Scrubbing onto one must still show WHERE the
// action happens, so the board rings the acting node.
//
// Steps with a real edge return null — the edge's current-step halo already
// carries the highlight, and double-marking the caller reads as noise.

import type { Module } from "@archlang/scene/arch-model";
import type { WalkResult } from "@archlang/engine";
import { moduleKey } from "@archlang/scene/arch-model";

/**
 * Board item ids of the current step's participants — caller and callee
 * resolved to modules (equal for self-referential rows). Null when the step
 * has no resolvable endpoints (ownerless prose, extern actors). Drives the
 * step auto-framing: the viewport must show BOTH.
 */
export function stepParticipants(
  walk: WalkResult | null,
  activeStepIndex: number | null,
  modules: readonly Module[],
): { aId: string; bId: string } | null {
  if (!walk || activeStepIndex === null) return null;
  const step = walk.byIndex.get(activeStepIndex);
  if (!step) return null;
  const resolve = makeResolver(modules);
  const lane = step.from.length > 0 ? step.from : step.to;
  if (lane.length === 0) return null;
  const a = resolve(lane);
  if (!a) return null;
  const b = step.noteText === undefined && step.to.length > 0 ? resolve(step.to) : a;
  return b ? { aId: a, bId: b } : null;
}

/**
 * Board item id of the current step's acting node, or null when the step has
 * a real edge (or no resolvable lane — e.g. ownerless prose).
 */
export function stepActorId(
  walk: WalkResult | null,
  activeStepIndex: number | null,
  modules: readonly Module[],
): string | null {
  if (!walk || activeStepIndex === null) return null;
  const step = walk.byIndex.get(activeStepIndex);
  if (!step) return null;
  const lane = step.from.length > 0 ? step.from : step.to;
  if (lane.length === 0) return null; // ownerless flow prose — no actor

  const resolve = makeResolver(modules);
  const actor = resolve(lane);
  if (!actor) return null;
  // Note / await / go rows are single-lane by construction. A call is
  // self-referential when BOTH endpoints resolve to the same MODULE (the
  // callee path carries the interface segment, so compare resolved nodes,
  // not raw paths). Distinct endpoints → the edge halo carries the highlight.
  if (step.noteText === undefined) {
    const callee = step.to.length > 0 ? resolve(step.to) : null;
    if (callee !== actor) return null;
  }
  return actor;
}

/**
 * Lane-path → board item id resolver. Lane paths may end with the INTERFACE
 * segment and can be written as SHORT refs (hoisting: `Auth` for
 * `Platform.Auth`) — trim trailing segments, match full path or unique suffix.
 */
function makeResolver(modules: readonly Module[]): (qn: readonly string[]) => string | null {
  const flat: { key: string; path: string }[] = [];
  const collect = (ms: readonly Module[], parent: string): void => {
    for (const m of ms) {
      const path = parent ? `${parent}.${m.name}` : m.name;
      flat.push({ key: moduleKey(m), path });
      if (m.children?.length) collect(m.children, path);
    }
  };
  collect(modules, "");
  return (qn) => {
    for (let n = qn.length; n >= 1; n--) {
      const want = qn.slice(0, n).join(".");
      const exact = flat.find((f) => f.path === want);
      if (exact) return exact.key;
      const suffix = flat.filter((f) => f.path.endsWith(`.${want}`));
      if (suffix.length === 1) return suffix[0]!.key;
    }
    return null;
  };
}
